"use client";

import Cookies from "js-cookie";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import { COOKIE_NAME } from "@/constants/middleware.constants";
import type { IUser } from "@/models/User";

interface AuthSessionContextValue {
	sessionUser: IUser | undefined;
	setSessionUser: (user: IUser | undefined) => void;
	hasSessionToken: boolean;
}

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null);

function parseUserCookie(): IUser | undefined {
	const raw = Cookies.get(COOKIE_NAME);
	if (!raw) return undefined;

	try {
		return JSON.parse(raw) as IUser;
	} catch {
		try {
			return JSON.parse(decodeURIComponent(raw)) as IUser;
		} catch {
			return undefined;
		}
	}
}

export function AuthSessionProvider({ children }: { children: ReactNode }) {
	// Hydrate synchronously from cookie so first render already knows session state.
	const [sessionUser, setSessionUserState] = useState<IUser | undefined>(() =>
		parseUserCookie(),
	);

	const setSessionUser = useCallback((user: IUser | undefined) => {
		setSessionUserState(user);
	}, []);

	useEffect(() => {
		// Keep session state aligned with cookie changes across page visibility/focus.
		const syncFromCookie = () => {
			setSessionUserState(parseUserCookie());
		};

		const onVisibilityChange = () => {
			if (!document.hidden) syncFromCookie();
		};

		window.addEventListener("focus", syncFromCookie);
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			window.removeEventListener("focus", syncFromCookie);
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, []);

	const value = useMemo<AuthSessionContextValue>(
		() => ({
			sessionUser,
			setSessionUser,
			hasSessionToken: Boolean((sessionUser as any)?.token),
		}),
		[sessionUser, setSessionUser],
	);

	return (
		<AuthSessionContext.Provider value={value}>
			{children}
		</AuthSessionContext.Provider>
	);
}

export function useAuthSession() {
	const ctx = useContext(AuthSessionContext);
	if (!ctx) {
		throw new Error("useAuthSession must be used within AuthSessionProvider");
	}
	return ctx;
}

